import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { InsightsDashboardView } from '@/components/insights/InsightsDashboardView'
import {
  buildClusterLabelMap,
  selectBridgeGroups,
  selectClusterComposition,
  selectKpiSummary,
  selectScoreBuckets,
} from '@/components/insights/selectors'
import type {
  BridgeInsight,
  ComponentsResponse,
  GraphComponent,
  TopEntitiesResponse,
  TopEntityEntry,
} from '@/services/api/graph'
import { graphApi } from '@/services/api/graph'

export interface InsightsDashboardProps {
  caseId: string
  onNavigateToGraph?: (options: {
    componentId?: number
    componentIdB?: number
    nodeIds?: string[]
    label?: string
    entityType?: string
    entityName?: string
    bridgeNodeIds?: string[]
  }) => void
  onOpenDocument?: (documentId: string) => void
}

/**
 * Connected Insights dashboard. Owns the clusters/bridges/top-entities fetch,
 * the recompute action, and all derived selectors, then feeds the pure
 * {@link InsightsDashboardView}.
 */
export function InsightsDashboard({
  caseId,
  onNavigateToGraph,
  onOpenDocument,
}: InsightsDashboardProps) {
  const [components, setComponents] = useState<ComponentsResponse | null>(null)
  const [bridges, setBridges] = useState<BridgeInsight[]>([])
  const [topEntities, setTopEntities] = useState<TopEntitiesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [computing, setComputing] = useState(false)
  const [hadError, setHadError] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setHadError(false)
    try {
      const [comps, bridgeRes, topRes] = await Promise.all([
        graphApi.getComponents(caseId),
        graphApi.getBridges(caseId),
        graphApi.getTopEntities(caseId),
      ])
      setComponents(comps)
      setBridges(bridgeRes.bridges)
      setTopEntities(topRes)
    } catch (err) {
      setHadError(true)
      const msg = err instanceof Error ? err.message : 'Failed to load insights'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRecompute = useCallback(async () => {
    setComputing(true)
    try {
      await graphApi.computeAnalysis(caseId)
      await fetchData()
      toast.success('Analysis recomputed')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to recompute analysis'
      toast.error(msg)
    } finally {
      setComputing(false)
    }
  }, [caseId, fetchData])

  // ─── Derived data ────────────────────────────────────────────────────────
  const kpi = useMemo(
    () => selectKpiSummary(components, bridges, topEntities),
    [components, bridges, topEntities]
  )
  const composition = useMemo(() => selectClusterComposition(components), [components])
  const buckets = useMemo(() => selectScoreBuckets(topEntities), [topEntities])
  const bridgeGroups = useMemo(() => selectBridgeGroups(bridges), [bridges])
  const labelMap = useMemo(() => buildClusterLabelMap(components), [components])
  const labelFor = useCallback(
    (compId: number) => labelMap.get(compId) ?? `Cluster ${compId}`,
    [labelMap]
  )
  const biggestClusterLabel = useMemo(() => {
    if (kpi.biggestCluster == null) return null
    return `largest ${labelFor(kpi.biggestCluster.comp_id).replace('Cluster ', 'cluster ')} · ${kpi.biggestCluster.size} entities`
  }, [kpi.biggestCluster, labelFor])

  const isStale = components?.stale === true
  const hasComponents = (components?.components.length ?? 0) > 0
  const hasBridges = bridges.length > 0
  const hasTopEntities = (topEntities?.entity_types.length ?? 0) > 0
  const isEmpty = !hasComponents && !hasBridges && !hasTopEntities && !loading

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleNavigateCluster = useCallback(
    (component: GraphComponent, label: string) => {
      onNavigateToGraph?.({ componentId: component.comp_id, label })
    },
    [onNavigateToGraph]
  )

  const handleSelectCompositionCluster = useCallback(
    (compId: number) => {
      onNavigateToGraph?.({ componentId: compId, label: labelFor(compId) })
    },
    [onNavigateToGraph, labelFor]
  )

  const handleBridgeClick = useCallback(
    (bridge: BridgeInsight) => {
      onNavigateToGraph?.({
        componentId: bridge.component_a,
        componentIdB: bridge.component_b,
        label: `Bridge: ${bridge.entity_a_name} ↔ ${bridge.entity_b_name}`,
        bridgeNodeIds: [bridge.entity_a_id, bridge.entity_b_id],
      })
    },
    [onNavigateToGraph]
  )

  const handleRowClick = useCallback(
    (entityType: string, entity: TopEntityEntry) => {
      onNavigateToGraph?.({
        nodeIds: [entity.id],
        entityType,
        entityName: entity.name,
      })
    },
    [onNavigateToGraph]
  )

  return (
    <InsightsDashboardView
      caseId={caseId}
      loading={loading}
      computing={computing}
      hadError={hadError}
      isEmpty={isEmpty}
      isStale={isStale}
      hasComponents={hasComponents}
      components={components}
      topEntities={topEntities}
      kpi={kpi}
      composition={composition}
      buckets={buckets}
      bridgeGroups={bridgeGroups}
      totalBridges={bridges.length}
      biggestClusterLabel={biggestClusterLabel}
      labelFor={labelFor}
      onRecompute={handleRecompute}
      onRetry={fetchData}
      onSelectCompositionCluster={handleSelectCompositionCluster}
      onBridgeClick={handleBridgeClick}
      onRowClick={handleRowClick}
      onNavigateCluster={handleNavigateCluster}
      onOpenDocument={onOpenDocument}
    />
  )
}
